/**
 * Client pour l'Edge Function Cal.com Proxy
 * 
 * Cette couche d'abstraction fournit :
 * - Authentification automatique avec Supabase
 * - Cache intelligent avec TTL de 5 minutes (lecture seule)
 * - Déduplication des appels identiques en vol
 * - Retry automatique avec stratégie configurable
 * - Gestion d'erreurs robuste avec extraction des messages Cal.com
 * 
 * CACHE :
 * - Les opérations de lecture (getSlots) sont mises en cache
 * - Les mutations (create, cancel, reschedule) ne sont jamais mises en cache
 * - Le cache est nettoyé automatiquement toutes les 5 minutes
 * 
 * RETRY :
 * - Retry sur erreurs réseau et timeouts
 * - Pas de retry sur rate limiting (429)
 * - Maximum 2 tentatives pour éviter la surcharge
 * 
 * DEBUG :
 * - Exposé via window.calEdgeDebug en développement
 * - Permet de vider le cache et consulter les stats
 */

import { fetchWithRetry } from '@/utils/retry';
import { logger } from '@/utils/logger';
import { BookingError } from '@/types/errors';

const EDGE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cal-proxy`;

/**
 * Types d'opérations supportées par le proxy Cal.com
 */
type CalProxyOperation = 'getSlots' | 'createBooking' | 'cancelBooking' | 'rescheduleBooking' | 'callCalendlyCancelWebhookByBookingId';

/**
 * Structure d'une requête au proxy Cal.com
 */
interface CalProxyRequest {
  operation: CalProxyOperation;
  params: any;
}

/**
 * Structure d'une réponse du proxy Cal.com
 */
interface CalProxyResponse {
  success: boolean;
  data?: any;
  error?: any;
  status?: number;
  message?: string;
  alreadyCancelled?: boolean; // Flag spécial pour les annulations déjà effectuées
}

/**
 * Entrée en cache avec timestamp
 */
interface CachedResult {
  data: any;
  timestamp: number;
}

// Cache pour dédupliquer les appels en vol (promesses partagées)
const inflightRequests = new Map<string, Promise<CalProxyResponse>>();

// Cache pour les résultats (5 minutes TTL)
const RESULT_CACHE_TTL_MS = 5 * 60 * 1000;
const resultCache = new Map<string, CachedResult>();

/**
 * Génère une clé de cache unique pour un appel
 * 
 * @param request - Requête dont on veut générer la clé
 * @returns Clé de cache (opération + params sérialisés)
 * @private
 */
function getCacheKey(request: CalProxyRequest): string {
  return `${request.operation}:${JSON.stringify(request.params)}`;
}

/**
 * Vérifie si un résultat en cache est encore valide
 * 
 * @param cached - Entrée en cache à vérifier
 * @returns true si le cache est encore valide (< 5 minutes)
 * @private
 */
function isCacheValid(cached: CachedResult): boolean {
  return Date.now() - cached.timestamp < RESULT_CACHE_TTL_MS;
}

/**
 * Appelle l'Edge Function Cal.com Proxy avec déduplication et cache
 * 
 * FONCTIONNEMENT :
 * 1. Vérifie le cache de résultats (pour getSlots uniquement)
 * 2. Vérifie si un appel identique est déjà en cours (déduplication)
 * 3. Fait l'appel HTTP avec retry automatique
 * 4. Met à jour le cache si succès (pour getSlots uniquement)
 * 
 * RETRY STRATEGY :
 * - Maximum 2 tentatives
 * - Pas de retry sur rate limiting (429)
 * - Retry sur erreurs réseau et timeouts
 * - Retry sur erreurs serveur 5xx
 * 
 * GESTION D'ERREURS :
 * - Extrait les messages d'erreur Cal.com depuis la structure de réponse complexe
 * - Gère spécifiquement le rate limiting (429)
 * - Log structuré avec logger pour faciliter le debugging
 * 
 * @param request - Requête à envoyer au proxy (operation + params)
 * @returns {Promise<CalProxyResponse>} Réponse du proxy
 * 
 * @throws {BookingError} Si l'appel échoue après tous les retries
 * 
 * @example
 * const result = await callCalProxy({
 *   operation: 'getSlots',
 *   params: {
 *     start: '2025-01-01T00:00:00Z',
 *     end: '2025-01-31T23:59:59Z',
 *     eventTypeSlug: 'shootingfirst3',
 *     teamSlug: 'prsnl',
 *     timeZone: 'Europe/Paris'
 *   }
 * });
 * 
 * if (result.success) {
 *   console.log('Slots:', result.data);
 * }
 * 
 * @public
 */
export async function callCalProxy(request: CalProxyRequest): Promise<CalProxyResponse> {
  // Validation basique
  if (!request.operation || !request.params) {
    logger.error('Invalid request to Cal proxy', { request });
    throw new BookingError(
      'Invalid request: operation and params are required',
      'VALIDATION_ERROR',
      { request }
    );
  }

  const cacheKey = getCacheKey(request);
  
  // 1️⃣ Vérifier le cache de résultats (sauf pour les mutations)
  if (request.operation === 'getSlots') {
    const cached = resultCache.get(cacheKey);
    if (cached && isCacheValid(cached)) {
      return { success: true, data: cached.data };
    }
  }
  
  // 2️⃣ Vérifier si un appel identique est déjà en cours
  const existing = inflightRequests.get(cacheKey);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    try {
      const response = await fetchWithRetry(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify(request),
      }, {
        maxAttempts: 2, // Réduire à 2 tentatives pour éviter trop d'appels sur 429
        initialDelayMs: 2000,
        shouldRetry: (error: any) => {
          // NE PAS retry sur 429 (rate limit)
          if (error?.message?.includes('429')) {
            return false;
          }
          // Retry sur erreurs réseau
          if (error?.message?.includes('fetch') || error?.message?.includes('network')) {
            return true;
          }
          // Retry sur timeout
          if (error?.name === 'AbortError' || error?.message?.includes('timeout')) {
            return true;
          }
          // Retry sur erreurs serveur 5xx
          if (error?.message?.includes('Server error')) {
            return true;
          }
          return false;
        }
      });

      if (!response.ok) {
        const errorData: any = await response.json().catch(() => ({}));
        
        // Gérer 429 spécifiquement
        if (response.status === 429) {
          logger.warn('⚠️ Rate limit atteint (429), veuillez patienter');
          throw new Error('Trop de demandes envoyées. Veuillez patienter quelques instants et réessayer.');
        }
        
        // Extraire le vrai message d'erreur Cal.com (utile pour détecter "déjà annulé", etc.)
        const calMessage =
          errorData?.error?.error?.message ||
          errorData?.error?.message ||
          errorData?.message ||
          response.statusText;
        
        logger.error('❌ Edge function error:', {
          status: response.status,
          statusText: response.statusText,
          errorData,
          calMessage,
        });
        
        throw new Error(`Edge function error: ${response.status} - ${calMessage}`);
      }

      const result = await response.json();
      
      // Sauvegarder en cache (sauf mutations)
      if (request.operation === 'getSlots' && result.success && result.data) {
        resultCache.set(cacheKey, {
          data: result.data,
          timestamp: Date.now()
        });
      }

      return result;
    } finally {
      // Nettoyer la promesse en vol
      inflightRequests.delete(cacheKey);
    }
  })();
  
  // Stocker la promesse en vol pour déduplication
  inflightRequests.set(cacheKey, promise);
  
  return promise;
}

/**
 * Récupère les créneaux disponibles pour un mois via l'Edge Function
 * 
 * UTILISATION :
 * - Appelle Cal.com v2 API via l'Edge Function sécurisée
 * - Les résultats sont automatiquement mis en cache (5 minutes)
 * - Supporte eventTypeId OU (eventTypeSlug + teamSlug)
 * 
 * PARAMÈTRES CAL.COM :
 * - start/end : Format ISO 8601 (pas startTime/endTime)
 * - eventTypeId : Recommandé pour meilleures performances
 * - eventTypeSlug + teamSlug : Alternative si eventTypeId non disponible
 * 
 * @param startTime - Date de début au format ISO 8601 (ex: "2025-01-01T00:00:00Z")
 * @param endTime - Date de fin au format ISO 8601 (ex: "2025-01-31T23:59:59Z")
 * @param eventTypeSlug - Slug de l'event type Cal.com (ex: "shootingfirst3")
 * @param teamSlug - Slug de l'équipe Cal.com (ex: "prsnl")
 * @param options - Options additionnelles
 * @param options.timeZone - Timezone IANA (défaut: timezone du navigateur)
 * @param options.eventTypeId - ID numérique de l'event type (recommandé)
 * @param options.username - Username Cal.com (optionnel)
 * 
 * @returns {Promise<any>} Données des slots depuis Cal.com
 * 
 * @throws {BookingError} Si la récupération échoue
 * 
 * @example
 * // Avec eventTypeId (recommandé)
 * const slots = await getMonthSlotsViaEdge(
 *   '2025-01-01T00:00:00Z',
 *   '2025-01-31T23:59:59Z',
 *   'shootingfirst3',
 *   'prsnl',
 *   { timeZone: 'Europe/Paris', eventTypeId: 3972153 }
 * );
 * 
 * // Avec slug uniquement
 * const slots = await getMonthSlotsViaEdge(
 *   '2025-01-01T00:00:00Z',
 *   '2025-01-31T23:59:59Z',
 *   'shootingfirst3',
 *   'prsnl'
 * );
 * 
 * @public
 */
export async function getMonthSlotsViaEdge(
  startTime: string,
  endTime: string,
  eventTypeSlug: string,
  teamSlug: string | undefined,
  options?: { timeZone?: string; eventTypeId?: number; username?: string }
): Promise<any> {
  // Validation des paramètres
  if (!startTime || !endTime) {
    throw new BookingError(
      'startTime and endTime are required',
      'VALIDATION_ERROR',
      { startTime, endTime }
    );
  }

  if (!eventTypeSlug) {
    throw new BookingError(
      'eventTypeSlug is required',
      'VALIDATION_ERROR',
      { eventTypeSlug }
    );
  }

  // If neither teamSlug nor username is provided, it's invalid
  if (!options?.username && !teamSlug) {
    throw new BookingError(
      'teamSlug or username is required with eventTypeSlug',
      'VALIDATION_ERROR',
      { eventTypeSlug, teamSlug, username: options?.username }
    );
  }

  const timeZone = options?.timeZone || (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'Europe/Paris');

  const result = await callCalProxy({
    operation: 'getSlots',
    params: {
      start: startTime,  // Cal.com v2 API expects 'start' not 'startTime'
      end: endTime,      // Cal.com v2 API expects 'end' not 'endTime'
      eventTypeSlug,
      teamSlug,
      timeZone,
      eventTypeId: options?.eventTypeId,
      username: options?.username,
    },
  });

  if (!result.success) {
    const errorMsg = result.error?.message || result.message || 'Failed to fetch slots';
    logger.error('Failed to get slots from edge', { error: errorMsg, params: { startTime, endTime, eventTypeSlug } });
    throw new BookingError(errorMsg, 'CAL_API_ERROR', { startTime, endTime, eventTypeSlug });
  }

  return result.data;
}

/**
 * Crée une réservation via l'Edge Function
 * 
 * UTILISATION :
 * - Appelle Cal.com v2 API POST /bookings
 * - Supporte eventTypeId (numérique) OU eventTypeSlug (+ teamSlug) pour les recurring
 * - Les métadonnées sont optionnelles mais recommandées
 * 
 * @param bookingData - Données de la réservation
 * @param bookingData.eventTypeId - ID numérique de l'event type (recommandé)
 * @param bookingData.eventTypeSlug - Slug de l'event type (utilisé si eventTypeId absent)
 * @param bookingData.teamSlug - Slug de l'équipe (optionnel mais recommandé avec eventTypeSlug)
 * @param bookingData.start - Date/heure de début ISO 8601 (requis)
 * @param bookingData.attendee - Informations du participant (requis)
 * @param bookingData.attendee.name - Nom du participant
 * @param bookingData.attendee.email - Email du participant
 * @param bookingData.attendee.timeZone - Timezone IANA du participant
 * @param bookingData.metadata - Métadonnées optionnelles (plan, etc.)
 * @param bookingData.guests - Emails des invités optionnels
 * @param bookingData.location - Localisation optionnelle
 * 
 * @returns {Promise<any>} Données de la réservation créée
 * 
 * @throws {BookingError} Si la création échoue
 * 
 * @example
 * const booking = await createBookingViaEdge({
 *   eventTypeId: 3972153,
 *   start: '2025-01-15T10:00:00Z',
 *   attendee: {
 *     name: 'John Doe',
 *     email: 'john@example.com',
 *     timeZone: 'Europe/Paris'
 *   },
 *   metadata: { plan: 'presence' }
 * });
 * 
 * @public
 */
export async function createBookingViaEdge(bookingData: any): Promise<any> {
  // Validation des champs requis
  if ((!bookingData.eventTypeId && !bookingData.eventTypeSlug) || !bookingData.start || !bookingData.attendee) {
    throw new BookingError(
      'eventTypeId or eventTypeSlug, start, and attendee are required',
      'VALIDATION_ERROR',
      { bookingData }
    );
  }

  if (!bookingData.attendee.email || !bookingData.attendee.name) {
    throw new BookingError(
      'attendee.email and attendee.name are required',
      'VALIDATION_ERROR',
      { attendee: bookingData.attendee }
    );
  }

  logger.info('Creating booking via edge', { 
    eventTypeId: bookingData.eventTypeId, 
    eventTypeSlug: bookingData.eventTypeSlug,
    attendeeEmail: bookingData.attendee.email 
  });

  const result = await callCalProxy({
    operation: 'createBooking',
    params: bookingData,
  });

  if (!result.success) {
    const errorMsg = result.error?.message || result.message || 'Failed to create booking';
    logger.error('Failed to create booking via edge', { error: errorMsg });
    throw new BookingError(errorMsg, 'CAL_API_ERROR', { bookingData });
  }

  logger.info('Booking created successfully via edge', { bookingUid: result.data?.uid });
  return result.data;
}

/**
 * Annule une réservation via l'Edge Function
 * 
 * UTILISATION :
 * - Appelle Cal.com v2 API POST /bookings/:uid/cancel
 * - Gère automatiquement le cas "déjà annulé"
 * 
 * CAS PARTICULIER :
 * - Si la réservation est déjà annulée côté Cal.com, l'edge function
 *   retourne success=true avec un flag alreadyCancelled=true
 * 
 * @param bookingUid - UID Cal.com de la réservation à annuler
 * @param cancellationReason - Raison de l'annulation
 * 
 * @returns {Promise<any>} Données de l'annulation
 * 
 * @throws {BookingError} Si l'annulation échoue
 * 
 * @example
 * try {
 *   const result = await cancelBookingViaEdge(
 *     '2aS5fYrUUmqhCk5DPB5HEL',
 *     'Changement de planning'
 *   );
 *   console.log('Booking cancelled:', result);
 * } catch (error) {
 *   console.error('Cancellation failed:', error);
 * }
 * 
 * @public
 */
export async function cancelBookingViaEdge(
  bookingUid: string,
  cancellationReason: string,
  metadata?: Record<string, any>
): Promise<any> {
  // Validation
  if (!bookingUid || bookingUid.trim().length === 0) {
    throw new BookingError(
      'bookingUid is required',
      'VALIDATION_ERROR',
      { bookingUid }
    );
  }

  logger.info('Cancelling booking via edge', { bookingUid, cancellationReason, metadata });

  const result = await callCalProxy({
    operation: 'cancelBooking',
    params: { 
      bookingUid, 
      cancellationReason,
      ...(metadata && { metadata }),
    },
  });

  if (!result.success) {
    const errorMsg = result.error?.message || result.message || 'Failed to cancel booking';
    const errorDetails = {
      bookingUid,
      status: result.status,
      error: result.error,
      message: errorMsg,
    };
    
    logger.error('Failed to cancel booking via edge', errorDetails);
    console.error('❌ Détails de l\'erreur d\'annulation:', errorDetails);
    
    // Message d'erreur plus explicite selon le code
    if (result.status === 404) {
      throw new BookingError(
        'Ce rendez-vous n\'existe pas ou a déjà été supprimé dans Cal.com',
        'BOOKING_NOT_FOUND',
        errorDetails
      );
    }
    
    throw new BookingError(errorMsg, 'CAL_API_ERROR', errorDetails);
  }

  if (result.alreadyCancelled) {
    logger.warn('Booking was already cancelled', { bookingUid });
  } else {
    logger.info('Booking cancelled successfully', { bookingUid });
  }

  return result.data;
}

// DEPRECATED only needed for Calendly service transition to Cal.com
export async function callCalendlyCancelWebhookByBookingIdViaEdge(bookingId: string): Promise<void> {
  const result = await callCalProxy({
    operation: 'callCalendlyCancelWebhookByBookingId',
    params: { bookingId },
  });
  if (!result.success) {
    logger.warn('Calendly cancel webhook by booking id failed', { bookingId, error: result.error ?? result.message });
  }
}

/**
 * Reprogramme une réservation via l'Edge Function
 * 
 * UTILISATION :
 * - Appelle Cal.com v2 API POST /bookings/:uid/reschedule
 * - Crée une nouvelle réservation avec un nouveau UID
 * - L'ancienne réservation est automatiquement annulée
 * 
 * @param bookingUid - UID Cal.com de la réservation à reprogrammer
 * @param reschedulingData - Données de reprogrammation
 * @param reschedulingData.start - Nouvelle date/heure ISO 8601 (requis)
 * @param reschedulingData.reschedulingReason - Raison (optionnel)
 * 
 * @returns {Promise<any>} Données de la nouvelle réservation (avec nouveau UID)
 * 
 * @throws {BookingError} Si la reprogrammation échoue
 * 
 * @example
 * const result = await rescheduleBookingViaEdge(
 *   '2aS5fYrUUmqhCk5DPB5HEL',
 *   {
 *     start: '2025-01-20T14:00:00Z',
 *     reschedulingReason: 'Demande client'
 *   }
 * );
 * 
 * console.log('New booking UID:', result.uid);
 * 
 * @public
 */
export async function rescheduleBookingViaEdge(
  bookingUid: string,
  reschedulingData: any
): Promise<any> {
  // Validation
  if (!bookingUid || bookingUid.trim().length === 0) {
    throw new BookingError(
      'bookingUid is required',
      'VALIDATION_ERROR',
      { bookingUid }
    );
  }

  if (!reschedulingData.start) {
    throw new BookingError(
      'reschedulingData.start is required',
      'VALIDATION_ERROR',
      { reschedulingData }
    );
  }

  logger.info('Rescheduling booking via edge', { bookingUid, newStart: reschedulingData.start });

  const result = await callCalProxy({
    operation: 'rescheduleBooking',
    params: { bookingUid, ...reschedulingData },
  });

  if (!result.success) {
    const errorMsg = result.error?.message || result.message || 'Failed to reschedule booking';
    logger.error('Failed to reschedule booking via edge', { error: errorMsg, bookingUid });
    throw new BookingError(errorMsg, 'CAL_API_ERROR', { bookingUid, reschedulingData });
  }

  logger.info('Booking rescheduled successfully', { 
    oldUid: bookingUid, 
    newUid: result.data?.uid 
  });

  return result.data;
}
